/**
 * Service for managing chat sessions with the chat API
 */

import { makeAuthenticatedRequest, createUrlWithParams } from '../utils/http';
import { logWarn } from '../utils/logger';
import { normalizeParlantEvent, normalizeParlantEvents } from '../utils/parlantEvent';
import type {
  ParlantEvent,
  MessageCreationParams,
  SessionMode,
  ModerationLevel
} from '../types/parlant';

/**
 * Consumption offsets for tracking message read state
 */
export interface ConsumptionOffsets {
  client: number;
}

/**
 * Interface for a chat session
 */
export interface Session {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  customer_id?: string;
  agent_id: string;
  mode: SessionMode;
  consumption_offsets: ConsumptionOffsets;
  metadata?: Record<string, unknown>;
  labels?: string[];
}

/**
 * Interface for session creation data
 */
export interface SessionCreationData {
  agent_id: string;
  customer_id?: string;
  title?: string;
  metadata?: Record<string, unknown>;
  labels?: string[];
}

/**
 * Interface for session update data
 */
export interface SessionUpdateData {
  title?: string;
  mode?: SessionMode;
  consumption_offsets?: {
    client?: number;
  };
  metadata?: Record<string, unknown>;
}

/**
 * Parameters for updating session labels
 */
export interface SessionLabelsUpdateData {
  /** Label IDs to add */
  add?: string[];
  /** Label IDs to remove */
  remove?: string[];
}

/**
 * Options for sending messages
 */
export interface SendMessageOptions {
  moderation?: ModerationLevel;
  metadata?: Record<string, unknown>;
  data?: unknown;
}

/**
 * Options for creating sessions
 */
export interface CreateSessionOptions {
  allowGreeting?: boolean;
}

/**
 * Options for reading a single event
 */
export interface ReadEventOptions {
  waitForCompletion?: boolean;
  waitForData?: number;
  sse?: boolean;
}

/**
 * Service class for session operations
 */
export class SessionService {
  private serverUrl: string;
  private authToken?: string;

  constructor(serverUrl: string, authToken?: string) {
    // Remove trailing slash from serverUrl to avoid double slashes in URLs
    this.serverUrl = serverUrl.endsWith('/') ? serverUrl.slice(0, -1) : serverUrl;
    this.authToken = authToken;
  }

/**
 * Fetches a single page of sessions.
 * @param customerId - Customer ID to filter sessions
 * @param opts - Optional cursor and page size for pagination
 * @returns Promise resolving to a page of sessions and an optional next cursor
 */
async fetchSessionsPage(
  customerId: string,
  opts?: { cursor?: string; limit?: number }
): Promise<{ items: Session[]; nextCursor: string | null }> {
  const params: Record<string, string | number> = { customer_id: customerId };
  if (opts?.cursor) params['cursor'] = opts.cursor;
  if (opts?.limit != null) params['limit'] = opts.limit;

  const url = createUrlWithParams(`${this.serverUrl}/sessions`, params);
  const raw = await makeAuthenticatedRequest<Session[] | { items: Session[]; next_cursor?: string | null }>(
    url, {}, this.authToken
  );

  // Handle both legacy array response and new paginated DTO
  if (Array.isArray(raw)) {
    return { items: raw, nextCursor: null };
  }
  return {
    items: raw.items,
    nextCursor: raw.next_cursor ?? null,
  };
}

/**
 * Fetches all sessions for a customer, transparently looping through pages.
 * @param customerId - Customer ID to filter sessions
 * @returns Promise resolving to array of all sessions
 */
async fetchSessions(customerId: string): Promise<Session[]> {
  const all: Session[] = [];
  let cursor: string | undefined;

  do {
    const page = await this.fetchSessionsPage(customerId, { cursor, limit: 100 });
    all.push(...page.items);
    cursor = page.nextCursor ?? undefined;
  } while (cursor);

  return all;
}

  /**
   * Creates a new session
   * @param sessionData - Session creation data
   * @param options - Optional query parameters (e.g. allow_greeting)
   * @returns Promise resolving to created session
   */
  async createSession(
    sessionData: SessionCreationData,
    options?: CreateSessionOptions
  ): Promise<Session> {
    const params: Record<string, string> = {};
    if (options?.allowGreeting != null) {
      params.allow_greeting = String(options.allowGreeting);
    }

    const url =
      Object.keys(params).length > 0
        ? createUrlWithParams(`${this.serverUrl}/sessions`, params)
        : `${this.serverUrl}/sessions`;

    return makeAuthenticatedRequest<Session>(
      url,
      {
        method: 'POST',
        body: sessionData,
      },
      this.authToken
    );
  }

  /**
   * Deletes a session
   * @param sessionId - ID of session to delete
   * @returns Promise resolving when deletion is complete
   */
  async deleteSession(sessionId: string): Promise<void> {
    await makeAuthenticatedRequest(
      `${this.serverUrl}/sessions/${sessionId}`,
      { method: 'DELETE' },
      this.authToken
    );
  }

  /**
   * Fetches events for a session
   * @param sessionId - Session ID
   * @param minOffset - Minimum offset for polling
   * @param waitForData - Seconds to wait for new data
   * @returns Promise resolving to array of events
   */
  async fetchEvents(
    sessionId: string,
    minOffset: number = 0,
    waitForData: number = 60
  ): Promise<ParlantEvent[]> {
    const url = createUrlWithParams(`${this.serverUrl}/sessions/${sessionId}/events`, {
      min_offset: minOffset,
      wait_for_data: waitForData
    });

    // Set timeout to be longer than waitForData to allow server to respond
    // Add 10 seconds buffer for network latency and processing
    const timeoutMs = (waitForData + 10) * 1000;

    const raw = await makeAuthenticatedRequest<Record<string, unknown>[]>(
      url,
      { timeout: timeoutMs },
      this.authToken
    );

    return normalizeParlantEvents(raw);
  }

  /**
   * Sends a message to a session
   * @param sessionId - Session ID
   * @param message - Message data
   * @param options - Optional settings like moderation level
   * @returns Promise resolving to created event
   */
  async sendMessage(
    sessionId: string,
    message: MessageCreationParams,
    options?: SendMessageOptions
  ): Promise<ParlantEvent> {
    const baseUrl = `${this.serverUrl}/sessions/${sessionId}/events`;
    const url = options?.moderation
      ? createUrlWithParams(baseUrl, { moderation: options.moderation })
      : baseUrl;

    const body: MessageCreationParams = {
      ...message,
      ...(options?.metadata != null ? { metadata: options.metadata } : {}),
      ...(options?.data !== undefined ? { data: options.data } : {}),
    };

    const raw = await makeAuthenticatedRequest<Record<string, unknown>>(
      url,
      {
        method: 'POST',
        body,
      },
      this.authToken
    );

    return normalizeParlantEvent(raw);
  }

  /**
   * Deletes events from a session starting at the given offset.
   */
  async deleteEvents(sessionId: string, minOffset: number): Promise<void> {
    const url = createUrlWithParams(`${this.serverUrl}/sessions/${sessionId}/events`, {
      min_offset: minOffset,
    });

    await makeAuthenticatedRequest(url, { method: 'DELETE' }, this.authToken);
  }

  /**
   * Reads a single event, optionally waiting for completion.
   */
  async readEvent(
    sessionId: string,
    eventId: string,
    options?: ReadEventOptions
  ): Promise<ParlantEvent> {
    const params: Record<string, string | number> = {};
    if (options?.waitForCompletion != null) {
      params.wait_for_completion = String(options.waitForCompletion);
    }
    if (options?.waitForData != null) {
      params.wait_for_data = String(options.waitForData);
    }
    if (options?.sse != null) {
      params.sse = String(options.sse);
    }

    const baseUrl = `${this.serverUrl}/sessions/${sessionId}/events/${eventId}`;
    const url =
      Object.keys(params).length > 0 ? createUrlWithParams(baseUrl, params) : baseUrl;

    const raw = await makeAuthenticatedRequest<Record<string, unknown>>(url, {}, this.authToken);
    return normalizeParlantEvent(raw);
  }

  /**
   * Updates a session
   * @param sessionId - Session ID
   * @param updateData - Data to update
   * @returns Promise resolving to updated session
   */
  async updateSession(sessionId: string, updateData: SessionUpdateData): Promise<Session> {
    return makeAuthenticatedRequest<Session>(
      `${this.serverUrl}/sessions/${sessionId}`,
      {
        method: 'PATCH',
        body: updateData
      },
      this.authToken
    );
  }

  /**
   * Updates labels on a session
   * @param sessionId - Session ID
   * @param labelsData - Labels to add/remove
   * @returns Promise resolving to updated session
   */
  async updateLabels(sessionId: string, labelsData: SessionLabelsUpdateData): Promise<Session> {
    return makeAuthenticatedRequest<Session>(
      `${this.serverUrl}/sessions/${sessionId}/labels`,
      {
        method: 'PATCH',
        body: labelsData
      },
      this.authToken
    );
  }

  /**
   * Checks backend connectivity via the /healthz endpoint
   * @returns Promise resolving to boolean indicating if backend is online
   */
  async pingBackend(): Promise<boolean> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);

      const response = await fetch(`${this.serverUrl}/healthz`, {
        signal: controller.signal,
        headers: this.authToken
          ? { Authorization: `Bearer ${this.authToken}` }
          : {},
      });

      clearTimeout(timeoutId);

      if (!response.ok) return false;

      const body = await response.json() as { status?: string };
      // healthy or degraded count as reachable
      return body.status === 'healthy' || body.status === 'degraded';
    } catch (error) {
      logWarn('Backend ping failed:', error);
      return false;
    }
  }
}
